// Decoder for the analysis-recommendation payload (contract 03 §6: decode at the `api/` boundary).
//
// Deliberately LENIENT where the rest of module 23's codecs are strict. v3 read this payload with
// bare optional chaining and rendered whatever arrived (`creativeAiRecommendations.ts`), so a strict
// decoder would turn a single malformed frame into an empty section — a visible regression against
// the behaviour this port is reproducing. The rules: the envelope must be an object and
// `recommendations` an array; a frame survives only if it has a `frame_id`; every other field
// degrades to its neutral value.
import type { CreativeKind } from './analysis.types'
import type {
  RecommendationExecution,
  RecommendationFilter,
  RecommendationFrame,
} from './recommendation.types'

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** `number | string | null` → `number | null`, dropping values that are not finite numbers.
 *  v3 used `Number.isNaN(Number(v))`, which admits `Infinity`; `Number.isFinite` does not — the
 *  stricter read is intentional, since an infinite spend renders as "∞" in every formatter. */
function numeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function creativeKind(value: unknown): CreativeKind | null {
  return value === 'image' || value === 'video' ? value : null
}

function filters(value: unknown): RecommendationFilter[] {
  if (!Array.isArray(value)) return []
  const result: RecommendationFilter[] = []
  for (const entry of value) {
    const row = asRecord(entry)
    const field = row ? text(row.field) : null
    if (!row || field === null) continue
    result.push({ field, value: row.value, match_mode: text(row.match_mode) ?? 'in' })
  }
  return result
}

function humanFilters(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

/** `frame_id` is the card key and the deep-link identity, so it is the one required field.
 *  Numeric ids are stringified here — v3 compared them with `String(...)` at every call site. */
function decodeFrame(value: unknown): RecommendationFrame | null {
  const row = asRecord(value)
  if (!row) return null
  const rawId = row.frame_id
  if (typeof rawId !== 'string' && typeof rawId !== 'number') return null
  const frameId = String(rawId)
  if (frameId === '') return null

  return {
    frame_id: frameId,
    filters: filters(row.filters),
    creative_type: creativeKind(row.creative_type),
    title: text(row.title),
    rationale_html: text(row.rationale_html),
    estimated_asset_count: numeric(row.estimated_asset_count),
    estimated_spend: numeric(row.estimated_spend),
    share_of_spend: numeric(row.share_of_spend),
    filters_human: humanFilters(row.filters_human),
    rank: numeric(row.rank),
    date_from: text(row.date_from),
    date_to: text(row.date_to),
  }
}

/**
 * Decode one recommendation execution. The backend wraps the payload in `{ result: … }` on this
 * endpoint but not consistently, so v3 read `res.data?.result ?? res.data`; that unwrap stays here
 * rather than in the api class so both endpoints share exactly one rule.
 */
export function decodeRecommendationExecution(value: unknown): RecommendationExecution {
  const envelope = asRecord(value)
  const body = (envelope && asRecord(envelope.result)) ?? envelope
  if (!body) throw new TypeError('recommendation execution must be an object')

  const rows = Array.isArray(body.recommendations) ? body.recommendations : []
  const recommendations: RecommendationFrame[] = []
  for (const row of rows) {
    const frame = decodeFrame(row)
    if (frame) recommendations.push(frame)
  }

  return {
    date_from: text(body.date_from),
    date_to: text(body.date_to),
    recommendations,
  }
}
